import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { TaskTarget } from '../../../networkPopUps/types';

export class ProtectedEntitiesHelper {
  public static isCompanyFolderOrCompanyCollection(record: ProtectedEntity): boolean {
    return [Constants.Companies.TYPE_COMPANIES_COLLECTION, Constants.Companies.TYPE_COMPANY_FOLDER].includes(
      record.type
    );
  }

  public static isNetworkCollection(record: ProtectedEntity): boolean {
    return record.type === Constants.Companies.TYPE_COMPANY_NETWORK;
  }

  public static isCompanion(record: ProtectedEntity): boolean {
    return record.specifics.productType === Constants.License.ProductType.COMPANION;
  }

  public static isManagedByHviAndNotIsBest(record: ProtectedEntity): boolean {
    return !!(record.specifics.isManagedByHvi && !record.specifics.isBest);
  }

  public static isWindowsOs(record: ProtectedEntity): boolean {
    return record.specifics.operatingSystem === Constants.OS.WIN;
  }

  public static isNixOs(record: ProtectedEntity): boolean {
    return record.specifics.operatingSystem === Constants.OS.NIX;
  }

  public static isCompany(record: ProtectedEntity): boolean {
    return record.type === Constants.Companies.TYPE_COMPANY;
  }

  public static isComputer(record: ProtectedEntity): boolean {
    return [
      Constants.ProtectedEntities.EPS.TYPE_COMPUTER,
      Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER,
      Constants.ProtectedEntities.EPS.TYPE_AWS_INSTANCE,
      Constants.ProtectedEntities.EPS.VMWARE_VIRTUAL_MACHINE,
    ].includes(record.type);
  }

  public static acceptsAdIntegration(record: ProtectedEntity): boolean {
    return [
      Constants.ProtectedEntities.EPS.TYPE_COMPUTER,
      Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER,
      Constants.ProtectedEntities.EPS.TYPE_AWS_INSTANCE,
    ].includes(record.type);
  }

  public static isUnifiedClient(record: ProtectedEntity): boolean {
    return record.specifics.appId === Constants.Endpoints.APP_IDS.APPID_UNIFIED_CLIENT;
  }

  public static isEpClient(record: ProtectedEntity): boolean {
    return record.specifics.appId === Constants.Endpoints.APP_IDS.APPID_EP_CLIENT;
  }

  public static isMacClient(record: ProtectedEntity): boolean {
    return record.specifics.appId === Constants.Endpoints.APP_IDS.APPID_MAC_CLIENT;
  }

  public static isContainerType(record: ProtectedEntity): boolean {
    return [
      Constants.ProtectedEntities.CONTAINERS.TYPE_COLLECTION,
      Constants.ProtectedEntities.CONTAINERS.TYPE_CUSTOM_GROUPS,
      Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER,
      Constants.ProtectedEntities.CONTAINERS.TYPE_HOST,
      Constants.ProtectedEntities.CONTAINERS.TYPE_CONTAINER,
    ].includes(record.type);
  }

  public static isAwsInstance(record: ProtectedEntity): boolean {
    return record.type === Constants.ProtectedEntities.EPS.TYPE_AWS_INSTANCE;
  }

  public static isPowerStateTerminated(record: ProtectedEntity): boolean {
    return record.specifics.stateCode === Constants.ProtectedEntities.AWSPowerState.POWER_STATE_TERMINATED;
  }

  public static hasMutualAuthRejected(record: ProtectedEntity): boolean {
    return record.specifics.maStatus === Constants.MutualAuth.MUTUAL_AUTH_TYPE_REJECTED;
  }

  public static isPartner(record: ProtectedEntity): boolean {
    return !!record.specifics.rights?.manageCompanies;
  }

  public static isSandboxAnalyzer(record: ProtectedEntity): boolean {
    return !!record.specifics.isSandboxAnalyzer;
  }

  public static isGhostr(record: ProtectedEntity): boolean {
    return !!record.specifics.isGhostr;
  }

  public static isSva(record: ProtectedEntity): boolean {
    return !!record.specifics.isSva;
  }

  public static isNsva(record: ProtectedEntity): boolean {
    return !!record.specifics.isNsva;
  }

  public static isProtectedBySecurityContainer(record: ProtectedEntity): boolean {
    return !!record.isProtectedBySecurityContainer;
  }

  public static isManaged(record: ProtectedEntity): boolean {
    return !!record.specifics.isManaged;
  }

  public static isNetworkCollectionOrFolder(record: ProtectedEntity): boolean {
    return [
      Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP,
      Constants.ProtectedEntities.EPS.CUSTOM_GROUPS,
      Constants.Companies.TYPE_COMPANY_NETWORK,
    ].includes(record.type);
  }

  public static isExcludedFolder(record: ProtectedEntity): boolean {
    return [Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER].includes(record.type);
  }

  public static isEditableFolder(record: ProtectedEntity): boolean {
    return [
      Constants.Companies.TYPE_COMPANY_FOLDER,
      Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP,
      Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER,
    ].includes(record.type);
  }

  public static isVirtual(record: ProtectedEntity): boolean {
    return !!record.specifics.isVirtual;
  }

  public static isGoldenImage(record: ProtectedEntity): boolean {
    return !!record.specifics.isGoldenImage;
  }

  public static isNetworkCustomFolder(record: ProtectedEntity): boolean {
    return record.type === Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP;
  }

  public static isCompanyFolder(record: ProtectedEntity) {
    return record.type === Constants.Companies.TYPE_COMPANY_FOLDER;
  }

  public static isContainerHostFolder(record: ProtectedEntity) {
    return record.type === Constants.ProtectedEntities.CONTAINERS.TYPE_HOST;
  }

  public static isContainerFolder(record: ProtectedEntity) {
    return record.type === Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER;
  }

  public static isEndpoint(record: ProtectedEntity | TaskTarget): boolean {
    if (this.isNetworkFolder(record)) {
      return false;
    }
    switch (record.type) {
      case Constants.Companies.TYPE_COMPANY:
      case Constants.Companies.TYPE_COMPANY_FOLDER:
      case Constants.Companies.TYPE_COMPANY_NETWORK:
      case Constants.Companies.TYPE_COMPANIES_COLLECTION:
        return false;
      default:
        return true;
    }
  }

  public static isContainer(record: ProtectedEntity): boolean {
    return record.type === Constants.ProtectedEntities.CONTAINERS.TYPE_CONTAINER;
  }

  public static isNetworkFolder(record: ProtectedEntity | TaskTarget): boolean {
    switch (record.type) {
      case Constants.ProtectedEntities.EPS.CUSTOM_GROUPS:
      case Constants.ProtectedEntities.EPS.TYPE_AD_COMPUTER_GROUP:
      case Constants.ProtectedEntities.EPS.ACTIVE_DIRECTORY:
      case Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP:
      case Constants.ProtectedEntities.EPS.TYPE_AWS_GROUP:
      case Constants.ProtectedEntities.EPS.TYPE_AWS_ACCOUNT:
      case Constants.ProtectedEntities.EPS.TYPE_AWS_REGION:
      case Constants.ProtectedEntities.EPS.TYPE_AWS_AZ:
      case Constants.ProtectedEntities.EPS.TERMINATED_INSTANCES_GROUP:
      case Constants.ProtectedEntities.CONTAINERS.TYPE_COLLECTION:
      case Constants.ProtectedEntities.CONTAINERS.TYPE_CUSTOM_GROUPS:
      case Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER:
      case Constants.ProtectedEntities.CONTAINERS.TYPE_HOST:
      case Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER:
      case Constants.ProtectedEntities.EPS.VMWARE_COLLECTION:
      case Constants.ProtectedEntities.EPS.VMWARE_CENTER:
      case Constants.ProtectedEntities.EPS.VMWARE_DATACENTER:
      case Constants.ProtectedEntities.EPS.VMWARE_FOLDER:
      case Constants.ProtectedEntities.EPS.VMWARE_COMPUTE_RESOURCE:
      case Constants.ProtectedEntities.EPS.VMWARE_CLUSTER_COMPUTE_RESOURCE:
      case Constants.ProtectedEntities.EPS.VMWARE_RESOURCE_POOL:
      case Constants.ProtectedEntities.EPS.VMWARE_VIRTUAL_APP:
      case Constants.ProtectedEntities.EPS.VMWARE_HOST_SYSTEM:
        return true;
      default:
        return false;
    }
  }
}
